import React from 'react';
import { Link } from 'react-router-dom';
import {
  Utensils, ShieldCheck, Leaf, Heart, Package,
  Users, ArrowRight, Check
} from 'lucide-react';
import { useSiteContent } from '../../hooks/useSiteContent';

const VALUE_ICONS = [Utensils, ShieldCheck, Leaf, Heart];

const FoodBucketSection = ({ costBreakdown }) => {
  const { data: cms } = useSiteContent('food_bucket_section', {});
  const valuePoints = cms.value_points || [];
  const items = cms.items || [];
  const tiers = cms.impact_tiers || [];

  return (
    <section className="bg-[#0A0A0A] text-white rounded-lg overflow-hidden border border-[#1F1F1F]" data-testid="food-bucket-section">
      <div className="p-6 md:p-10 grid lg:grid-cols-2 gap-8">
        {/* LEFT: Title + Value Points + Impact + CTA */}
        <div className="space-y-6">
          {/* Header */}
          <div>
            <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight leading-none">
              <span className="text-[#BA1826]">FOOD</span> BUCKET
            </h2>
            <p className="text-[10px] tracking-[0.35em] text-white/60 font-semibold mt-1">P R O G R A M</p>
            {cms.description && (
              <p className="text-sm text-white/80 mt-4 leading-relaxed max-w-md">{cms.description}</p>
            )}
          </div>

          {/* Value Points */}
          <div className="space-y-4">
            {valuePoints.map((vp, i) => {
              const Icon = VALUE_ICONS[i % VALUE_ICONS.length];
              return (
                <div key={i} className="flex items-start gap-3" data-testid={`food-value-${i}`}>
                  <div className="w-10 h-10 rounded-full border border-[#BA1826] flex items-center justify-center flex-shrink-0">
                    <Icon className="w-4 h-4 text-[#BA1826]" />
                  </div>
                  <div>
                    <h4 className="font-heading text-sm font-bold uppercase tracking-wide text-[#BA1826]">{vp.heading}</h4>
                    <p className="text-xs text-white/75 leading-relaxed mt-0.5">{vp.text}</p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* YOUR IMPACT */}
          <div className="border-t border-b border-[#BA1826]/60 py-4">
            <h3 className="font-heading text-xs font-bold uppercase tracking-[0.3em] text-[#BA1826] text-center mb-3">Your Impact</h3>
            <div className="space-y-2">
              {tiers.map((t, i) => (
                <div key={i} className="flex items-center justify-between text-sm border-b border-dotted border-white/10 last:border-0 pb-1.5" data-testid={`food-impact-tier-${i}`}>
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-[#BA1826]" />
                    <span className="font-semibold">{t.families} FAMILIES</span>
                  </div>
                  <span className="font-semibold">${Number(t.amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="border border-[#BA1826] rounded-lg p-4 flex items-center gap-4" data-testid="food-cta-block">
            <Heart className="w-8 h-8 text-[#BA1826]" fill="#BA1826" />
            <div className="flex-1">
              <p className="font-heading text-lg font-bold text-[#BA1826]">{cms.cta_amount_label}</p>
              <p className="text-xs text-white/70">{cms.cta_sub_text}</p>
            </div>
            <Link to="/donate?amount=50" className="bg-[#BA1826] hover:bg-[#8B0F1C] text-white px-4 py-2 rounded font-semibold text-xs uppercase tracking-wider flex items-center gap-1" data-testid="food-donate-btn">
              Donate <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
        </div>

        {/* RIGHT: "What's Inside" items + checklist */}
        <div className="space-y-6">
          <div>
            <h3 className="font-heading text-sm font-bold uppercase tracking-[0.2em] text-[#BA1826] text-center border-y border-[#BA1826]/50 py-2 mb-6">
              What's Inside Each Bucket
            </h3>
            <div className="grid grid-cols-5 gap-3 text-center">
              {items.map((item, i) => (
                <div key={i} className="space-y-1.5" data-testid={`food-item-${i}`}>
                  <div className="w-14 h-14 mx-auto rounded-full border border-[#BA1826] flex items-center justify-center">
                    <Package className="w-6 h-6 text-[#BA1826]" />
                  </div>
                  <p className="text-[10px] font-semibold uppercase tracking-wider">{item.name}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Checklist highlights from value points */}
          <div className="space-y-2">
            {valuePoints.map((vp, i) => (
              <div key={i} className="flex items-center gap-2 text-xs text-white/85">
                <Check className="w-3.5 h-3.5 text-[#BA1826] flex-shrink-0" strokeWidth={3} />
                <span>{vp.text}</span>
              </div>
            ))}
          </div>

          {/* TOGETHER WE CAN */}
          <div className="text-center border-t border-b border-[#BA1826]/60 py-4">
            <h4 className="font-heading text-xs font-bold uppercase tracking-[0.3em] text-[#BA1826] mb-1">Together, We Can</h4>
            <p className="text-lg italic text-white/90" style={{ fontFamily: 'Georgia, serif' }}>Turn Compassion into Action.</p>
          </div>
        </div>
      </div>

      {/* Transparency Breakdown (existing cost table, repurposed inside this section) */}
      {costBreakdown}
    </section>
  );
};

export default FoodBucketSection;
